import clsx from "clsx";
import { GatsbyImage } from "gatsby-plugin-image";
import { uniqBy } from "lodash";
import React, { FC, Fragment, useCallback, useEffect, useMemo } from "react";

import { useProductsComparator } from "../../../../contexts/products-comparator";
import { useDataLayer } from "../../../../hooks/use-data-layer";
import { GoogleAnalyticsEvent } from "../../../../utils/analytics";
import { ProductTechSheetRenderer } from "../../../../utils/products";
import { Modal, ModalProps } from "../../../modal";
import { ProductComparatorItemCtas } from "./product-comparator-item-ctas";

export interface ProductComparatorDetailProps extends ModalProps {
  labels: {
    "product-comparator-detail-title": string;
    "product-comparator-detail-details": string;
    "product-comparator-detail-request-info": string;
    "product-comparator-detail-missing-field": string;
  };
}

const columnClassName = "col-span-1";

export const ProductComparatorDetail: FC<ProductComparatorDetailProps> = ({ labels, ...rest }) => {
  const { products, selectedProductsCount, maxProducts } = useProductsComparator();

  const allProductsTechSheetFields = useMemo(() => {
    return uniqBy(
      products
        ?.flatMap((product) => product?.specialization)
        .flatMap((specialization) => specialization?.techSheet)
        .filter(Boolean),
      "internalName"
    );
  }, [products]);

  const { isOpen } = rest;
  const { pushEvent } = useDataLayer();
  useEffect(() => {
    if (isOpen) {
      const twoProductsEvent = {
        event: GoogleAnalyticsEvent.ProductCompare2,
        product_name: products[0]?.defaultLocaleEntry?.name ?? null,
        product_name2: products[1]?.defaultLocaleEntry?.name ?? null,
      };
      const threeProductsEvent = {
        event: GoogleAnalyticsEvent.ProductCompare3,
        product_name: products[0]?.defaultLocaleEntry?.name ?? null,
        product_name2: products[1]?.defaultLocaleEntry?.name ?? null,
        product_name3: products[2]?.defaultLocaleEntry?.name ?? null,
      };
      pushEvent(selectedProductsCount === 3 ? threeProductsEvent : twoProductsEvent);
    }
  }, [isOpen, selectedProductsCount, pushEvent, products]);

  const onClickProductDetails = useCallback(
    ({ defaultLocaleEntry }: Queries.ProductDataFragment) => {
      if (defaultLocaleEntry) {
        pushEvent(
          {
            event: GoogleAnalyticsEvent.ClickProduct,
            product_name: defaultLocaleEntry?.name,
            product_id: defaultLocaleEntry?.id,
            product_category: defaultLocaleEntry.specialization?.name ?? null,
            product_category2: defaultLocaleEntry.specialization?.machiningType?.name ?? null,
            product_category3: defaultLocaleEntry.specialization?.machiningType?.material ?? null,
            click_selection: "Comparator",
          },
          ["product_id"]
        );
      }
    },
    [pushEvent]
  );

  return (
    <Modal fullScreen {...rest}>
      <div className="p-4 flex flex-col gap-6 lg:gap-8 relative">
        <h3 className="text-2xl lg:text-4xl text-primary text-left lg:text-center font-bold">
          {labels["product-comparator-detail-title"]} {`(${selectedProductsCount}/${maxProducts})`}
        </h3>
        <div className="w-full overflow-x-auto primary-scrollbar horizontal-scrollbar pb-10">
          <div
            className={clsx(
              "grid gap-4 lg:gap-6 w-full lg:justify-center",
              selectedProductsCount === 2 && "grid-cols-[repeat(2,_minmax(256px,370px))]",
              selectedProductsCount === 3 && "grid-cols-[repeat(3,_minmax(256px,370px))]"
            )}
          >
            {/* Images row */}
            {products
              .filter(Boolean)
              .map(
                (product, i) =>
                  product?.cardImages?.[0]?.gatsbyImageData && (
                    <GatsbyImage
                      image={product?.cardImages[0]?.gatsbyImageData}
                      key={i}
                      alt={product.name ?? ""}
                      className={clsx("rounded-xl", columnClassName)}
                    />
                  )
              )}
            {/* Titles row */}
            {products.filter(Boolean).map((product, i) => (
              <h3
                className={clsx(
                  "text-xl font-bold text-left lg:text-center w-full hyphens-auto",
                  columnClassName
                )}
                key={i}
              >
                {product?.name}
              </h3>
            ))}
            {/* Buttons row */}
            {products.filter(Boolean).map((product, i) => (
              <ProductComparatorItemCtas
                key={i}
                product={product}
                columnClassName={columnClassName}
                labels={labels}
                detailOnClick={() => product && onClickProductDetails(product)}
              />
            ))}
            {/* Tech sheet row */}
            {allProductsTechSheetFields.map((field, i) => {
              if (!field?.internalName) return null;
              const renderedFields = products.map((product) => {
                if (!product) return [null, null];
                const renderer = new ProductTechSheetRenderer(product, labels);
                const attribute = field.internalName ?? "";
                return [
                  renderer.renderFieldLabel(attribute),
                  renderer.renderFieldValueNode(attribute),
                ];
              });
              // Skip all empty rows
              if (renderedFields.every(([, v]) => !v)) {
                return null;
              }
              // label could not be available for individual products if they lack that particular field
              // so we retrieve it once from the first product it features it
              const label = renderedFields.map(([label]) => label).filter(Boolean)[0];
              return (
                <Fragment key={i}>
                  {renderedFields.slice(0, selectedProductsCount).map(([, value], j) => (
                    <div className="text-left lg:text-center" key={j}>
                      <strong>{label}</strong>
                      {value ?? <p>{labels["product-comparator-detail-missing-field"]}</p>}
                    </div>
                  ))}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
};
